/**
 * AIFundraiserChat.tsx - AI-Powered Fundraiser Creation Chat Interface
 *
 * This component provides a conversational interface for creating fundraisers using AI.
 * It guides users through a streamlined process to collect essential information
 * and generates comprehensive fundraiser content automatically.
 *
 * Key Features:
 * - Conversational chat interface with typing indicators
 * - Smart analysis of initial fundraiser description
 * - Dynamic follow-up questions for missing information
 * - AI-generated content for all required sections
 * - Manual creation option for users who prefer traditional forms
 * - Smooth animations and professional chat UI
 *
 * Flow:
 * 1. Initial prompt: "Describe your fundraiser."
 * 2. AI analysis to extract key information
 * 3. Dynamic questions for missing critical data
 * 4. Generate all required fields using OpenAI
 * 5. Preview and proceed to builder with populated data
 *
 * Props:
 * - isVisible: boolean - Controls modal visibility
 * - onClose: () => void - Close modal handler
 * - onComplete: (fundraiserData: any) => void - Completion handler with generated data
 * - onManualCreate: () => void - Manual creation handler
 *
 * Dependencies: OpenAI service, MobX store, styled-components
 */

import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import { X, Send, Bot, User, Loader, Sparkles } from "lucide-react";
import { OpenAIService } from "../../utils/openaiService";
import { fundraiserStore } from "../../stores";
import {
  ColorModel,
  CreateFundraiserModel,
  FundraiserDonationButtonColors,
  FundraiserSectionVisibilityModel,
  FundRaiserStatsModel,
  MediaModel,
} from "../../models";
import {
  ConfigurationStore,
  IAPIDownloadMediaRequestModel,
  MediaItem,
  MediaType,
  SharedLibraryService,
  UnsplashService,
} from "@mindmemobile/mm-shared-library";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../../services";
import { AuthService } from "@mindmemobile/mm-shared-global";
import {
  ImpactModel,
  RankAchievementModel,
  RewardModel,
} from "../../models/FundraisersGridModels";
import { RewardByRank } from "../../interfaces";
import { IconType } from "../../enums";
import MediaService from "../../services/MediaService";
import {
  ApplicationStatus,
  MerchantAccount,
  OpenAISettingsDataModel,
  RGBA,
} from "@mindmemobile/mm-payment-modules";
import { ApplicationSettingsStore } from "@mindmemobile/mm-payment-modules";
import { canManageFundraiser } from "../../utils/permissions";

const Overlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

const Modal = styled.div<{ visible: boolean }>`
  background: #ffffff;
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  height: 80vh;
  max-height: 700px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: ${(props) =>
    props.visible ? "scale(1) translateY(0)" : "scale(0.9) translateY(20px)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 27px 24px 27px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px 20px 0 0;
  color: white;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: 700;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  color: white;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled.div<{ isUser: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  ${(props) => props.isUser && "flex-direction: row-reverse;"}
`;

const MessageAvatar = styled.div<{ isUser: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${(props) => (props.isUser ? "50%" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.isUser ? "#3b82f6" : "transparent")};
  color: white;
  flex-shrink: 0;
  border: ${(props) => (props.isUser ? "none" : "none")};
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  background: ${(props) => (props.isUser ? "#555555" : "#f1f5f9")};
  color: ${(props) => (props.isUser ? "white" : "#1e293b")};
  padding: 16px 20px;
  border-radius: 20px;
  max-width: 70%;
  line-height: 1.5;
  font-size: 18px;
  ${(props) => props.isUser && "border-bottom-right-radius: 8px;"}
  ${(props) => !props.isUser && "border-bottom-left-radius: 8px;"}
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    animation: typing 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }

  @keyframes typing {
    0%,
    80%,
    100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const InputArea = styled.div`
  padding: 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 20px 20px;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TextInput = styled.textarea`
  flex: 1;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 20px;
  resize: none;
  min-height: 100px;
  max-height: 170px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SendButton = styled.button<{ disabled: boolean }>`
  background: ${(props) =>
    props.disabled
      ? "#e2e8f0"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  color: ${(props) => (props.disabled ? "#9ca3af" : "white")};
  border: none;
  border-radius: 12px;
  padding: 16px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 16px 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  animation: progressAnimation 2s ease-in-out infinite;
  width: 100%;
  transform-origin: left;

  @keyframes progressAnimation {
    0% {
      transform: scaleX(0.3) translateX(-100%);
    }
    50% {
      transform: scaleX(0.8) translateX(0%);
    }
    100% {
      transform: scaleX(0.3) translateX(100%);
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding: 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 20px 20px;
`;

const ActionButton = styled.button<{
  variant: "primary" | "secondary" | "tertiary";
}>`
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;

  ${(props) => {
    if (props.variant === "primary") {
      return `
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-color: transparent;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
        }
      `;
    } else if (props.variant === "secondary") {
      return `
        background: transparent;
        color: #667eea;
        border-color: #667eea;
        
        &:hover {
          background: rgba(102, 126, 234, 0.1);
          transform: translateY(-1px);
        }
      `;
    } else {
      return `
        background: transparent;
        color: #64748b;
        border-color: #e2e8f0;
        
        &:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }
      `;
    }
  }}
`;

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface AIFundraiserChatProps {
  isVisible: boolean;
  onClose: () => void;
  onComplete: (fundraiserData: CreateFundraiserModel) => void;
  onManualCreate: () => void;
  merchantAccounts: MerchantAccount[];
  onAICreate?: () => void;
}

export const AIFundraiserChat: React.FC<AIFundraiserChatProps> = ({
  isVisible,
  onClose,
  onComplete,
  onManualCreate,
  merchantAccounts,
  onAICreate,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversationData, setConversationData] =
    useState<CreateFundraiserModel>(new CreateFundraiserModel());
  const [currentStep, setCurrentStep] = useState<
    | "initial"
    | "questions"
    | "marketing"
    | "image-question"
    | "donation-name"
    | "generating"
    | "complete"
  >("initial");
  const [lastAIMessageIndex, setLastAIMessageIndex] = useState(-1);
  const [pendingQuestions, setPendingQuestions] = useState<string[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState<string[]>([]);
  const [initialDescription, setInitialDescription] = useState("");
  const [wantsImage, setWantsImage] = useState<boolean | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<string[]>([]);
  const [answeredFields, setAnsweredFields] = useState<string[]>([]);
  const [marketingStep, setMarketingStep] = useState<
    "promoters" | "rewards-amount" | "rewards-rank" | "complete"
  >("promoters");
  const [wantsPromoters, setWantsPromoters] = useState<boolean | null>(null);
  const [wantsRewards, setWantsRewards] = useState<boolean | null>(null);
  const [rewardsByAmount, setRewardsByAmount] = useState<RewardModel[]>([]);
  const [rewardsByRank, setRewardsByRank] = useState<RankAchievementModel[]>(
    []
  );
  const [donationName, setDonationName] = useState("");
  const [fundraiserImage, setFundraiserImage] = useState<MediaModel | null>(
    null
  );
  const [hasFundraiserError, setHasFundraiserError] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const configurationStoreRef = useRef<ConfigurationStore | null>(null);

  if (!configurationStoreRef.current) {
    configurationStoreRef.current = new ConfigurationStore({
      userId: AuthService.mindmeAccountDetails?.userId || "",
      subAccountId: AuthService.mindmeAccountDetails?.subAccountId || "",
      parentAccountId: AuthService.mindmeAccountDetails?.parentAccountId || "",
      allowedMediaTypes: ["Image"],
      apiBaseURL: process.env.REACT_APP_MINDME_MAIN_API || "",
      handleAuth: true,
    });
  }
  const openaiService = useMemo(() => {
    return new OpenAIService(
      ApplicationSettingsStore.getOpenAISettings() ||
        new OpenAISettingsDataModel()
    );
  }, [ApplicationSettingsStore.getOpenAISettings()]);

  useEffect(() => {
    configurationStoreRef.current?.updateBearerToken();
    fundraiserStore.getAccountBussinessInfo();
  }, []);

  useEffect(() => {
    if (isVisible) {
      resetConversation();
      const welcomeMessage: Message = {
        id: "1",
        text: "Tell us about your fundraiser in detail. Include your mission, who will benefit, fundraising goal and prior year results and we'll create it for you!",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isVisible]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const addMessage = (text: string, isUser: boolean) => {
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const newMessage: Message = {
      id: uniqueId,
      text,
      isUser,
      timestamp: new Date(),
    };
    setMessages((prev) => {
      const newMessages = [...prev, newMessage];
      if (!isUser) {
        setLastAIMessageIndex(newMessages.length - 1);
      }
      return newMessages;
    });

    // Build conversation history for AI context
    if (isUser) {
      setConversationHistory((prev) => [...prev, `User: ${text}`]);
    } else {
      setConversationHistory((prev) => [...prev, `AI: ${text}`]);
      // Focus input after AI message
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const replaceLastMessage = (newText: string) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;

      const updatedMessages = [...prev];
      const lastMessage = updatedMessages[updatedMessages.length - 1];

      // Only replace if the last message was from AI
      if (!lastMessage.isUser) {
        updatedMessages[updatedMessages.length - 1] = {
          ...lastMessage,
          text: newText,
        };

        // Update conversation history too
        setConversationHistory((prevHistory) => {
          const updatedHistory = [...prevHistory];
          if (
            updatedHistory.length > 0 &&
            updatedHistory[updatedHistory.length - 1].startsWith("AI:")
          ) {
            updatedHistory[updatedHistory.length - 1] = `AI: ${newText}`;
          }
          return updatedHistory;
        });

        // Focus input after message replacement
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }

      return updatedMessages;
    });
  };

  const simulateTyping = async (duration: number = 1500) => {
    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, duration));
    setIsTyping(false);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    addMessage(userMessage, true);
    setIsProcessing(true);

    try {
      if (currentStep === "initial") {
        setInitialDescription(userMessage);
        await analyzeInitialDescription(userMessage);
      } else if (currentStep === "questions") {
        await handleQuestionAnswer(userMessage);
      } else if (currentStep === "marketing") {
        if (marketingStep === "rewards-amount" && wantsRewards) {
          await processRewardsByAmount(userMessage);
        } else {
          await handleMarketingAnswer(userMessage);
        }
      } else if (currentStep === "image-question") {
        await handleImageQuestion(userMessage);
      } else if (currentStep === "donation-name") {
        await handleDonationNameAnswer(userMessage);
      }
    } catch (error) {
      console.error("Error processing message:", error);
      addMessage(
        "I'm sorry, there was an error processing your request. Please try again.",
        false
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const analyzeInitialDescription = async (description: string) => {
    try {
      setIsTyping(true);
      // Don't use simulateTyping here - keep typing indicator until AI responds

      if (!openaiService.hasValidApiKey()) {
        setIsTyping(false);
        addMessage(
          "We had an issue creating your fundraiser. Please create it manually.",
          false
        );
        setHasFundraiserError(true);
        setCurrentStep("complete");
        setConversationData(new CreateFundraiserModel());
        return;
      }

      // Continue with normal analysis - typing indicator will be turned off when AI responds
      await continueAnalysisWithDescription(description);
    } catch (error) {
      console.error("Error analyzing description:", error);
      setIsTyping(false);
      setHasFundraiserError(true);
      addMessage(
        "We had an issue creating your fundraiser. Please create it manually.",
        false
      );
      setCurrentStep("complete");
      setConversationData(new CreateFundraiserModel());
    }
  };

  const continueAnalysisWithDescription = async (description: string) => {
    try {
      const { OpenAIService } = await import("../../utils/openaiService");
      // Extract fields that are already answered from the description
      const extractedFields = extractAnsweredFields(description);
      setAnsweredFields((prev) =>
        Array.from(new Set([...prev, ...extractedFields]))
      );

      const analysisResult =
        await openaiService.analyzeDescriptionAndGetQuestions(
          description,
          conversationHistory,
          answeredFields
        );

      if (!analysisResult.success) {
        console.error("❌ Analysis Error Details:", {
          error: analysisResult.error,
          description: description,
          descriptionLength: description.length,
        });

        openaiService.listGPT5Models().then((result) => {
          if (result.success) {
            console.log("📋 GPT-5 Models Test Results:", result.data);
          } else {
            console.error("❌ Failed to test GPT-5 models:", result.error);
          }
        });

        setIsTyping(false);
        setHasFundraiserError(true);
        addMessage(
          "We had an issue creating your fundraiser. Please create it manually.",
          false
        );
        setCurrentStep("complete");
        setConversationData(new CreateFundraiserModel());
        return;
      }

      const analysis = analysisResult.data;

      if (
        analysis.hasEnoughInfo ||
        !analysis.questions ||
        analysis.questions.length === 0
      ) {
        // Keep typing indicator until we're ready to show the next message
        addMessage("Perfect! I have the main details.", false);
        setIsTyping(false);
        // Move to marketing questions
        setTimeout(async () => {
          startMarketingQuestions();
        }, 1000);
      } else {
        setPendingQuestions(analysis.questions);
        setCurrentQuestionIndex(0);
        setCurrentStep("questions");

        // Keep typing indicator until we're ready to show the next message
        addMessage(
          "I need a bit more information to create your fundraiser.",
          false
        );
        setIsTyping(false);

        setTimeout(() => {
          addMessage(analysis.questions[0], false);
        }, 1000);
      }
    } catch (error) {
      console.error("Error analyzing description:", error);
      setIsTyping(false);
      setHasFundraiserError(true);
      addMessage(
        "We had an issue creating your fundraiser. Please create it manually.",
        false
      );
      setCurrentStep("complete");
      setConversationData(new CreateFundraiserModel());
    }
  };

  const handleQuestionAnswer = async (answer: string) => {
    const newAnswers = [...collectedAnswers, answer];
    setCollectedAnswers(newAnswers);

    // Check if user said no promoters - if so, skip any remaining promoter-related questions
    const noPromoters =
      answer.toLowerCase().includes("no promoter") ||
      answer.toLowerCase().includes("no promoters") ||
      (answer.toLowerCase() === "no" &&
        pendingQuestions[currentQuestionIndex]
          ?.toLowerCase()
          .includes("promoter"));

    if (noPromoters) {
      // Skip to image question
      await simulateTyping(800);
      addMessage(
        "Perfect! Since you won't be using promoters, let's move on.",
        false
      );
      setTimeout(async () => {
        addMessage(
          "Do you want us to select an image for your fundraiser?",
          false
        );
        setCurrentStep("image-question");
      }, 1000);
      return;
    }

    const nextQuestionIndex = currentQuestionIndex + 1;

    if (nextQuestionIndex < pendingQuestions.length) {
      // Check if the next question is about promoter incentives but user already said no promoters
      const nextQuestion = pendingQuestions[nextQuestionIndex];
      const isPromoterIncentiveQuestion =
        nextQuestion?.toLowerCase().includes("incentive") ||
        nextQuestion?.toLowerCase().includes("reward") ||
        nextQuestion?.toLowerCase().includes("promoter");

      if (
        isPromoterIncentiveQuestion &&
        newAnswers.some(
          (ans) =>
            ans.toLowerCase().includes("no promoter") ||
            ans.toLowerCase().includes("no promoters")
        )
      ) {
        // Skip to image question
        await simulateTyping(800);
        addMessage(
          "Do you want us to select an image for your fundraiser?",
          false
        );
        setCurrentStep("image-question");
        return;
      }

      setCurrentQuestionIndex(nextQuestionIndex);
      await simulateTyping(800);
      addMessage(pendingQuestions[nextQuestionIndex], false);
    } else {
      // All dynamic questions answered, now move to marketing questions
      await simulateTyping(800);
      startMarketingQuestions();
    }
  };

  const handleDonationNameAnswer = async (answer: string) => {
    if (!conversationData) return;

    const trimmedName = answer.trim();
    await simulateTyping(800);
    addMessage(
      `Great! I've set the donation item name as "${trimmedName}".`,
      false
    );

    await simulateTyping(800);
    addMessage("Your fundraiser setup is complete!", false);

    setCurrentStep("complete");
    await proceedToGeneration(
      initialDescription,
      collectedAnswers,
      trimmedName
    );
  };

  const handleImageQuestion = async (answer: string) => {
    const wantsImageSelection =
      answer.toLowerCase().includes("yes") ||
      answer.toLowerCase().includes("sure") ||
      answer.toLowerCase().includes("ok") ||
      answer.toLowerCase().includes("please");

    setWantsImage(wantsImageSelection);

    // Only proceed if generation was successful (conversationData is set)
    if (conversationData && Object.keys(conversationData).length > 0) {
      if (wantsImageSelection) {
        await simulateTyping(800);
        addMessage(
          "Great! I'll find the perfect image for your fundraiser.",
          false
        );
        await selectImageAndComplete();
      } else {
        await simulateTyping(800);
        addMessage("No problem! We'll skip the image for now.", false);

        // Always ask for donation name next
        await simulateTyping(800);
        addMessage("What would you like to name your donation item?", false);
        setCurrentStep("donation-name");
      }
    }
    // If generation failed, the error message was already shown by replaceLastMessage
  };

  const selectImageAndComplete = async () => {
    try {
      setIsGeneratingImage(true);
      setIsTyping(true);
      addMessage("Searching for the perfect image...", false);

      const fundraiserData = conversationData;
      const { UnsplashService1 } = await import("../../utils/unsplashService");

      // 1️⃣ Search image
      const imageResult = await UnsplashService1.searchFundraiserImage(
        initialDescription,
        fundraiserData.title || fundraiserData.name,
        fundraiserData.category.name
      );

      if (!imageResult.success) {
        setIsGeneratingImage(false);
        setIsTyping(false);
        addMessage(
          "I couldn't find a suitable image, but your fundraiser is ready.",
          false
        );
        completeWithoutImage();
        return;
      }

      const unsplashImage = imageResult.data as any;
      const imageUrl = UnsplashService1.getOptimizedImageUrl(
        unsplashImage,
        "regular"
      );
      const attribution = UnsplashService1.getAttributionText(unsplashImage);

      // 2️⃣ CALL DOWNLOAD API BEFORE SETTING ANY STATE
      const requestModel: IAPIDownloadMediaRequestModel = {
        subAccountId: AuthService.mindmeAccountDetails?.subAccountId || "",
        userId: AuthService.mindmeAccountDetails?.userId || "",
        medias: [
          {
            url: imageUrl,
            mediaType: MediaType.Image,
            mediaSource: "Unsplash",
            fileDuration: 0,
          },
        ],
      };
      if (!configurationStoreRef.current) return;
      let unsplashImageDownloaded: MediaItem | null = null;

      try {
        const response = await firstValueFrom(
          new UnsplashService(
            configurationStoreRef.current
          ).downloadUnsplashImageByUrl(requestModel)
        );

        if (response?.length > 0) {
          const mappedImages =
            response.map((img) => MediaItem.deserializeAPIModel(img)) || [];
          unsplashImageDownloaded = mappedImages[0];
        }
      } catch (error) {
        console.error("Error saving enhanced image:", error);
        unsplashImageDownloaded = null;
      }

      if (unsplashImageDownloaded) {
        setFundraiserImage(
          new MediaModel({
            fullURL: unsplashImageDownloaded.url,
            mediaId: unsplashImageDownloaded.id,
            type: MediaType.Image,
            title: "Fundraiser Image",
            description: `${attribution} - ${
              unsplashImage.alt_description || ""
            }`,
            s3Key: unsplashImageDownloaded.s3Key || "",
          })
        );
      }

      setIsGeneratingImage(false);
      setIsTyping(false);

      // 4️⃣ Do NOT replace last message
      addMessage("Perfect! I found a great image for your fundraiser.", false);

      // 5️⃣ Ask donation name
      await simulateTyping(800);
      addMessage("What would you like to name your donation item?", false);
      setCurrentStep("donation-name");
    } catch (error) {
      console.error("💥 Error selecting image:", error);
      setIsGeneratingImage(false);
      setIsTyping(false);
      addMessage(
        "I had trouble finding an image, but your fundraiser is ready with everything else.",
        false
      );
      await simulateTyping(800);
      addMessage("What would you like to name your donation item?", false);
      setCurrentStep("donation-name");
    }
  };

  const completeWithoutImage = async () => {
    await simulateTyping(800);
    addMessage("What would you like to name your donation item?", false);
    setCurrentStep("donation-name");
  };

  const proceedToGeneration = async (
    description: string,
    answers: string[],
    donationName: string
  ) => {
    if (!canManageFundraiser("Create")) return;
    setCurrentStep("generating");
    await generateFundraiserContent(description, answers, donationName);
  };

  const generateFundraiserContent = async (
    description: string,
    additionalAnswers: string[] = [],
    donationName: string
  ) => {
    try {
      // Start typing indicator and keep it until we have a response
      setIsTyping(true);
      // Remove the 1 second delay to speed up the process

      if (!openaiService.hasValidApiKey()) {
        setIsTyping(false);
        setHasFundraiserError(true);
        replaceLastMessage(
          "We had an issue creating your fundraiser. Please create it manually."
        );
        setCurrentStep("complete");
        setConversationData(new CreateFundraiserModel());
        return;
      }
      // Show generation message but keep typing indicator
      addMessage("Generating your fundraiser content...", false);
      // Add timeout to prevent hanging (60 seconds for text generation)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Generation timeout")), 60000)
      );

      const result = (await Promise.race([
        OpenAIService.generateFundraiserContent(
          description,
          conversationHistory,
          additionalAnswers,
          ApplicationSettingsStore.getOpenAISettings()
        ),
        timeoutPromise,
      ])) as any;

      if (!result.success) {
        console.error("OpenAI Error:", result.error);
        setIsTyping(false);
        setHasFundraiserError(true);
        replaceLastMessage(
          "We had an issue creating your fundraiser. Please create it manually."
        );
        setCurrentStep("complete");
        setConversationData(new CreateFundraiserModel());
        return;
      }

      const aiData = result.data;
      const activeMerchantAccounts = merchantAccounts.filter(
        (m) => m.applicationStatus === ApplicationStatus.Active
      );
      const activeMerchantAccount =
        activeMerchantAccounts.find((m) => m.isDefault) ||
        activeMerchantAccounts[0] ||
        null;

      const extractedGoal = aiData.main?.goal || aiData.goal || 10000;

      fundraiserStore.initializeBuilder();
      const fundraiserData = new CreateFundraiserModel({
        ...fundraiserStore.builderData,
        name: aiData.main?.name || "New Fundraiser",
        category: aiData.main?.category || "Community & Social",
        title: aiData.main?.title || "Help Make a Difference",
        subtitle: aiData.main?.subtitle || "Supporting our community",
        description: aiData.main?.description || description,
        startDate: aiData.main?.startDate
          ? new Date(aiData.main?.startDate)
          : undefined,
        endDate: aiData.main?.endDate
          ? new Date(aiData.main?.endDate)
          : undefined,
        createdDate: new Date(),
        missionTitle: aiData.mission?.title || "Our Mission",
        missionDescription:
          aiData.mission?.description ||
          "Making a positive impact in our community.",
        impact:
          aiData.impact?.map(
            (item: any, index: number) =>
              new ImpactModel({
                id: (index + 1).toString(),
                icon: "Heart",
                color: item.color || "#10b981",
                number: item.number || "100+",
                description: item.description || "Lives impacted",
                order: index + 1,
              })
          ) || [],
        fundraiserStats: aiData.stats
          ? aiData.stats.map(
              (item: any, index: number) =>
                new FundRaiserStatsModel({
                  id: (index + 1).toString(),
                  label: item.name || `Stat${index}`,
                  value: item.value || 0,
                })
            )
          : [],
        goal: extractedGoal,
        promoterGoal: Math.round(extractedGoal * 0.1), // 1/10 of fundraiser goal (e.g., $25,000 fundraiser = $2,500 promoter goal)
        allowPromoterInvites: wantsPromoters === true,
        rewards:
          rewardsByAmount.length > 0
            ? rewardsByAmount.map((r) => new RewardModel(r))
            : [],
        ranks:
          rewardsByRank.length > 0
            ? rewardsByRank.map((r) => new RankAchievementModel(r))
            : [],
        fundraiserImage: fundraiserImage || undefined,
        mediaItems: fundraiserImage?.fullURL ? [fundraiserImage] : [],
        ctaButtonText: "Donate",
        ctaTitle: "Make a Difference Today",
        ctaDescription:
          "Your donation is critically important to achieving our fundraising goals.",
        ctaIcon: "Heart",
        ctaIconImageChoice: "Icon",
        ctaImage: undefined,
        ctaIconColor: new ColorModel({
          hex: "#12B981",
          rgba: "rgba(18, 185, 129, 1)",
          rgbValue: new RGBA({ r: "18", g: "185", b: "129", a: "1" }),
        }),
        isCreateNewDonationItem: true,
        newDonationItemName: donationName || "General Donation",
        iconImageChoice: fundraiserImage?.fullURL
          ? IconType.Image
          : IconType.Icon,
        titleIcon: fundraiserImage?.fullURL ? "Heart" : undefined,
        merchantAccount: activeMerchantAccount
          ? activeMerchantAccount
          : undefined,
        donationButtonColors: new FundraiserDonationButtonColors({}),
        sectionVisibility: new FundraiserSectionVisibilityModel({ cta: true }),
      });

      const response = await firstValueFrom(
        FundraiserService.createFundraiser(
          CreateFundraiserModel.createAPIRequest(
            AuthService.mindmeAccountDetails?.userId || "",
            AuthService.authData.accountId,
            fundraiserData,
            fundraiserStore.layoutZones
          )
        )
      );
      if (response.result) {
        setConversationData(
          new CreateFundraiserModel({
            ...fundraiserData,
            id: response.result,
          })
        );
        setIsTyping(false);
        setCurrentStep("complete");
      } else {
        setHasFundraiserError(true);
        setIsTyping(false);
        replaceLastMessage(
          "We had an issue creating your fundraiser. Please create it manually."
        );
        setCurrentStep("complete");
        setConversationData(new CreateFundraiserModel());
      }
      // Turn off typing indicator and replace the generation message
    } catch (error) {
      console.error("Error generating content:", error);
      setHasFundraiserError(true);
      setIsTyping(false);

      if (error instanceof Error && error.message === "Generation timeout") {
        replaceLastMessage(
          "Generation took too long. Please create your fundraiser manually."
        );
      } else {
        replaceLastMessage(
          "We had an issue creating your fundraiser. Please create it manually."
        );
      }

      setCurrentStep("complete");
      setConversationData(new CreateFundraiserModel());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const ensureUniqueFundraiserName = (
    baseName: string,
    existingNames: string[]
  ): string => {
    const currentYear = new Date().getFullYear();
    let uniqueName = baseName;
    let counter = 0;

    while (
      existingNames.some(
        (name) => name.toLowerCase() === uniqueName.toLowerCase()
      )
    ) {
      counter++;
      if (counter === 1) {
        uniqueName = `${baseName} ${currentYear}`;
      } else {
        uniqueName = `${baseName} ${currentYear} (${counter - 1})`;
      }
    }

    return uniqueName;
  };

  const handleComplete = () => {
    if (conversationData) {
      const existingNames = fundraiserStore.fundraisers.map((f) => f.name);
      const uniqueName = ensureUniqueFundraiserName(
        conversationData.name,
        existingNames
      );
      const updatedData = new CreateFundraiserModel({
        ...conversationData,
        name: uniqueName,
      });
      onComplete(updatedData);
    }
  };

  const extractAnsweredFields = (text: string): string[] => {
    const fields: string[] = [];
    const lowerText = text.toLowerCase();

    // Check for goal amount
    if (/\$[\d,]+|[\d,]+\s*dollars?|\d+k/i.test(text)) {
      fields.push("goal");
    }

    // Check for dates/timeline
    if (/start|end|begin|finish|by\s+\w+|\w+\s+\d+|timeline/i.test(text)) {
      fields.push("timeline");
    }

    // Check for beneficiaries
    if (/team|organization|benefit|help|support|for\s+\w+/i.test(text)) {
      fields.push("beneficiaries");
    }

    // Check for fund usage
    if (
      /used?\s+for|funds?\s+for|money\s+for|purchase|buy|equipment|travel|uniforms/i.test(
        text
      )
    ) {
      fields.push("fundUsage");
    }

    // Check for mission/purpose
    if (/mission|purpose|cause|reason|why|help|support/i.test(text)) {
      fields.push("mission");
    }

    // Check for promoter strategy
    if (/promoter|individual|no\s+promoter|fundraiser/i.test(text)) {
      fields.push("promoterStrategy");
    }
    return fields;
  };

  const startMarketingQuestions = () => {
    setCurrentStep("marketing");
    setMarketingStep("promoters");
    addMessage("Will you invite people to promote your fundraiser?", false);
  };

  const handleMarketingAnswer = async (answer: string) => {
    const lowerAnswer = answer.toLowerCase();

    if (marketingStep === "promoters") {
      const wantsPromotersResponse =
        lowerAnswer.includes("yes") ||
        lowerAnswer.includes("sure") ||
        lowerAnswer.includes("ok") ||
        lowerAnswer.includes("definitely");
      setWantsPromoters(wantsPromotersResponse);

      if (wantsPromotersResponse) {
        setMarketingStep("rewards-amount");
        await simulateTyping(800);
        addMessage(
          "You can offer promoters individual rewards by the amount raised or based on the rank achieved. Would you like to add this now?",
          false
        );
      } else {
        // Skip to image question
        await simulateTyping(800);
        addMessage(
          "Do you want us to select an image for your fundraiser?",
          false
        );
        setCurrentStep("image-question");
      }
    } else if (marketingStep === "rewards-amount") {
      const wantsRewardsResponse =
        lowerAnswer.includes("yes") ||
        lowerAnswer.includes("sure") ||
        lowerAnswer.includes("ok") ||
        lowerAnswer.includes("definitely");
      setWantsRewards(wantsRewardsResponse);

      if (wantsRewardsResponse) {
        await simulateTyping(800);
        addMessage(
          'What rewards do promoters earn at specific donation amounts? For example, get a "tee shirt" if you raise $500 and a gift card if you raise $2,500. Reply "none" to add later.',
          false
        );
      } else {
        // Skip to image question
        await simulateTyping(800);
        addMessage(
          "Do you want us to select an image for your fundraiser?",
          false
        );
        setCurrentStep("image-question");
      }
    } else if (marketingStep === "rewards-rank") {
      // Handle rewards by rank
      if (
        lowerAnswer.includes("none") ||
        lowerAnswer.includes("skip") ||
        lowerAnswer.includes("later") ||
        lowerAnswer.includes("no thanks") ||
        lowerAnswer === "no"
      ) {
        // Skip to image question
        await simulateTyping(800);
        addMessage(
          "Do you want us to select an image for your fundraiser?",
          false
        );
        setCurrentStep("image-question");
      } else {
        // Process rewards by rank with AI
        await processRewardsByRank(answer);
      }
    }
  };

  const processRewardsByAmount = async (answer: string) => {
    if (
      answer.toLowerCase().includes("none") ||
      answer.toLowerCase().includes("skip") ||
      answer.toLowerCase().includes("later") ||
      answer.toLowerCase().includes("no thanks") ||
      answer.toLowerCase() === "no"
    ) {
      // Move to rank rewards question
      setMarketingStep("rewards-rank");
      await simulateTyping(800);
      addMessage(
        'What rewards do promoters earn if in the top # promoters? For example, get a $100 gift card if they rank #1 and raise a minimum of $2,500 or a tee shirt if top #2-10. Reply "none" to add later.',
        false
      );
      return;
    }

    try {
      setIsTyping(true);
      const result = await openaiService.parseRewardsByAmount(answer);

      if (result.success && result.data && result.data.length > 0) {
        setRewardsByAmount(
          result.data.map(
            (r: any) =>
              new RewardModel({
                level: r.level,
                reward: r.reward,
                order: r.order,
              })
          )
        );
        setIsTyping(false);
        addMessage(
          `Great! I've added ${result.data.length} reward${result.data.length > 1 ? "s" : ""} by amount.`,
          false
        );

        // Move to rank rewards question
        setMarketingStep("rewards-rank");
        setTimeout(() => {
          addMessage(
            'What rewards do promoters earn if in the top # promoters? For example, get a $100 gift card if they rank #1 and raise a minimum of $2,500 or a tee shirt if top #2-10. Reply "none" to add later.',
            false
          );
        }, 1000);
      } else {
        console.error("❌ Failed to parse rewards by amount:", result);
        setIsTyping(false);
        addMessage(
          "I had trouble understanding those rewards. Could you try rephrasing? For example: 'Shirt at $100, Lunch at $500'",
          false
        );
        // Don't move to next step, let user try again
      }
    } catch (error) {
      console.error("Error processing rewards by amount:", error);
      setIsTyping(false);
      addMessage(
        "I had trouble processing those rewards. Let's move on.",
        false
      );
      setMarketingStep("rewards-rank");
      setTimeout(() => {
        addMessage(
          'What rewards do promoters earn if in the top # promoters? Reply "none" to add later.',
          false
        );
      }, 1000);
    }
  };

  const processRewardsByRank = async (answer: string) => {
    try {
      setIsTyping(true);
      const { OpenAIService } = await import("../../utils/openaiService");

      const result = await openaiService.parseRewardsByRank(answer);

      if (result.success && result.data && result.data.length > 0) {
        setRewardsByRank(
          result.data.map(
            (r) =>
              new RankAchievementModel({
                rank: r.rank.replace("#", ""),
                minimum: r.minimum,
                reward: r.reward,
                order: r.order,
              })
          )
        );
        setIsTyping(false);
        addMessage(
          `Perfect! I've added ${result.data.length} rank-based reward${result.data.length > 1 ? "s" : ""}.`,
          false
        );

        // Move to image question
        setTimeout(() => {
          addMessage(
            "Do you want us to select an image for your fundraiser?",
            false
          );
          setCurrentStep("image-question");
        }, 1000);
      } else {
        console.error("❌ Failed to parse rewards by rank:", result);
        setIsTyping(false);
        addMessage(
          "I had trouble understanding those rank rewards. Could you try rephrasing? For example: '#1 gets trip, must raise $2500'",
          false
        );
        // Don't move to next step, let user try again
      }
    } catch (error) {
      console.error("Error processing rewards by rank:", error);
      setIsTyping(false);
      addMessage(
        "I had trouble processing those rewards, but let's continue.",
        false
      );
      setTimeout(() => {
        addMessage(
          "Do you want us to select an image for your fundraiser?",
          false
        );
        setCurrentStep("image-question");
      }, 1000);
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setInputValue("");
    setIsTyping(false);
    setIsProcessing(false);
    setConversationData(new CreateFundraiserModel());
    setCurrentStep("initial");
    setLastAIMessageIndex(-1);
    setPendingQuestions([]);
    setCurrentQuestionIndex(0);
    setCollectedAnswers([]);
    setInitialDescription("");
    setWantsImage(null);
    setIsGeneratingImage(false);
    setConversationHistory([]);
    setAnsweredFields([]);
    setMarketingStep("promoters");
    setWantsPromoters(null);
    setWantsRewards(null);
    setRewardsByAmount([]);
    setRewardsByRank([]);
  };

  const handleClose = () => {
    resetConversation();
    onClose();
  };

  const handleStartOver = () => {
    onAICreate && onAICreate();
    resetConversation();
    const welcomeMessage: Message = {
      id: "1",
      text: "Tell us about your fundraiser in detail. Include your mission, who will benefit, fundraising goal and prior year results and we'll create it for you!",
      isUser: false,
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
  };

  return (
    <Overlay
      visible={isVisible}
      onClick={(e) => {
        if (
          e.target === e.currentTarget &&
          !window.getSelection()?.toString()
        ) {
          handleClose();
        }
      }}
    >
      <Modal visible={isVisible} onMouseDown={(e) => e.stopPropagation()}>
        <Header>
          <HeaderContent>
            <Sparkles size={28} />
            <div>
              <Title>New Fundraiser</Title>
            </div>
          </HeaderContent>
          {currentStep !== "complete" ? (
            <HeaderButtons>
              <HeaderButton onClick={onManualCreate}>
                Create Manually
              </HeaderButton>
              <HeaderButton onClick={handleClose}>Cancel</HeaderButton>
            </HeaderButtons>
          ) : null}
        </Header>

        <ChatContainer>
          <MessagesArea>
            {messages.map((message, index) => {
              const showAvatar =
                !message.isUser &&
                (index === 0 ||
                  messages[index - 1].isUser ||
                  index === lastAIMessageIndex);

              return (
                <Message key={message.id} isUser={message.isUser}>
                  <MessageBubble isUser={message.isUser}>
                    {message.text}
                    {(message.text === "Searching for the perfect image..." ||
                      message.text ===
                        "Generating your fundraiser content...") &&
                      (isGeneratingImage || currentStep === "generating") && (
                        <ProgressBarContainer>
                          <ProgressBar>
                            <ProgressFill />
                          </ProgressBar>
                        </ProgressBarContainer>
                      )}
                  </MessageBubble>
                </Message>
              );
            })}

            {isTyping && !isGeneratingImage && currentStep !== "generating" && (
              <Message isUser={false}>
                <TypingIndicator>
                  <TypingDots>
                    <span></span>
                    <span></span>
                    <span></span>
                  </TypingDots>
                </TypingIndicator>
              </Message>
            )}

            <div ref={messagesEndRef} />
          </MessagesArea>

          {currentStep !== "complete" && (
            <InputArea>
              <InputContainer>
                <TextInput
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your answer..."
                  disabled={isProcessing || currentStep === "generating"}
                />
                <SendButton
                  onClick={handleSendMessage}
                  disabled={
                    !inputValue.trim() ||
                    isProcessing ||
                    currentStep === "generating"
                  }
                >
                  {isProcessing ? (
                    <Loader size={20} className="animate-spin" />
                  ) : (
                    <Send size={20} />
                  )}
                </SendButton>
              </InputContainer>
            </InputArea>
          )}
        </ChatContainer>

        {currentStep === "complete" && (
          <ActionButtons>
            {!hasFundraiserError ? (
              <>
                <ActionButton variant="primary" onClick={handleComplete}>
                  View Your Fundraiser
                </ActionButton>
                <ActionButton variant="secondary" onClick={handleStartOver}>
                  Start Over
                </ActionButton>
              </>
            ) : (
              <>
                <ActionButton variant="primary" onClick={onManualCreate}>
                  Create Manually
                </ActionButton>
                <ActionButton variant="secondary" onClick={handleStartOver}>
                  Start Over
                </ActionButton>
              </>
            )}
          </ActionButtons>
        )}
      </Modal>
    </Overlay>
  );
};
